<template lang="pug">
	.app-core--dropdown.flex-row
		.flex-col.space.flex-1(v-if="!isGrid")
			slot(name="top")
			DxSelectBox(
				ref="selectBox",
				:search-enabled="true",
				:data-source="source",
				search-mode="contains",
				:search-expr="displayExpr",
				:show-data-before-search="true",
				:valueExpr="valueExpr",
				:value="selectedValue",
				:display-expr="displayExpr",
				:placeholder="placeholder",
				:disabled="!!disabled",
				:buttons="buttons",
				:openOnFieldClick="false",
				:dropDownOptions="dropDownOptions || { minWidth: '360px' }",
				@selection-changed="e => onSelectionChanged(e, true)",
				@opened="v => setOpened(true)",
				@closed="v => setOpened(false)",
				:noDataText="`${searchText || ''}`.length < minSearchLength ? noDataText : null"
			)
				slot
			slot(name="bottom")
		DxDropDownBox.flex-1(
			v-else-if="!isRefreshing",
			ref="dropdown",
			:data-source="source"
			v-model:value="selectedValue",
			:defer-rendering="true"
			:display-expr="displayExpr"
			:show-clear-button="false"
			:value-expr="valueExpr"
			:placeholder="placeholder",
			:dropDownOptions="dropDownOptions || { minWidth: '400px', minHeight: '320px' }",
			:buttons="buttons",
			:disabled="!!disabled",
			:width="width",
			:hint="hint || selectedHint",
			:stylingMode="stylingMode",
			@opened="v => setOpened(true)",
			@closed="v => setOpened(false)"
		)
			template(:#content="{ data }")
				.flex-col.space.full-width.full-height.app-core--dropdown(:class="{ 'has-top': !!$scopedSlots.top, 'has-bottom': !!$scopedSlots.top }")
					slot(name="top")
					template(v-if="$scopedSlots.dropdown")
						slot(name="dropdown")
					DxDataGrid(
						v-else,
						ref="grid",
						:disabled="disabled",
						:data-source="source"
						:columns="columns"
						:hover-state-enabled="true"
						:selected-row-keys.sync="innerSelectedItems"
						:height="`calc(100% - ${30 * (($scopedSlots.top ? 1 : 0) + ($scopedSlots.bottom ? 1 : 0))}px`",
						:keyExpr="valueExpr",
						:showColumnHeaders="showColumnHeaders",
						:searchPanel="searchPanelSettings",
						@editor-preparing="onEditorPreparing",
						@selection-changed="onSelectionChanged",
						:visible="opened",
						:noDataText="`${searchText || ''}`.length <= minSearchLength ? noDataText : null"
					)
						DxSelection(:mode="multiple ? 'multiple' : 'single'", :allowSelectAll="multiple", showCheckBoxesMode="always")
						DxPaging(:enabled="true", :page-size="pageSize")
						DxFilterRow(:visible="filtersVisible")
						DxScrolling(mode="infinite")
						DxRemoteOperations(:paging="true", :filtering="true")
						template(v-for="(_, slot) of $scopedSlots" v-slot:[slot]="{ data }")
							slot(:name="slot" v-bind="data")
					slot(name="bottom")
			slot
</template>

<script>
	import $ from 'jquery';
	import _ from 'lodash';
	import VueTypes from 'vue-types';
	import DxDataGrid, { DxColumn, DxFilterRow, DxLookup, DxPager, DxPaging, DxHeaderFilter, DxSelection, DxRemoteOperations, DxScrolling } from 'devextreme-vue/data-grid';
	import DxDropDownBox from 'devextreme-vue/drop-down-box';
	import DataSource from 'devextreme/data/data_source';
	import Promise from 'bluebird';
	import { DxSelectBox } from 'devextreme-vue/select-box';

	export default {
		components: {
			DxDropDownBox,
			DxDataGrid, DxColumn, DxFilterRow, DxLookup, DxPager, DxPaging, DxHeaderFilter, DxSelection, DxRemoteOperations, DxScrolling, DxSelectBox
		},
		model: {
			prop: 'selectedItems',
			event: 'change'
		},
		props: {
			getTotal: VueTypes.func.def(null),
			getItems: VueTypes.func.def(null),
			getData: VueTypes.func.def(null),
			columns: VueTypes.array.def(null),
			displayExpr: VueTypes.string.def(null),
			valueExpr: VueTypes.string.def(null),
			placeholder: VueTypes.string.def(null),
			selectedItems: VueTypes.any.def(null),
			pageSize: VueTypes.number.def(10),
			showColumnHeaders: VueTypes.bool.def(true),
			multiple: VueTypes.bool.def(true),
			filtersVisible: VueTypes.bool.def(true),
			searchVisible: VueTypes.bool.def(false),
			displayText: VueTypes.any.def(null),
			getByKey: VueTypes.any.def(null),
			minSearchLength: VueTypes.number.def(0),
			disabled: VueTypes.bool.def(false),
			noDataText: VueTypes.string.def(null),
			dropDownOptions: VueTypes.any.def(null),
			width: VueTypes.number.def(240),
			preloadedObjects: VueTypes.array.def([]),
			watchReload: VueTypes.any.def(null),
			hint: VueTypes.string.def(null),
			stylingMode: VueTypes.string.def(null),
			showClearButton: VueTypes.bool.def(true),
			reloadOnOpen: VueTypes.bool.def(false)
		},
		data() {
			if (__DEBUG__)
				window.dd = this;
			let innerSelectedItems = this.multiple ? (_.clone(this.selectedItems) || []) : (this.selectedItems ? [this.selectedItems] : []);
			let clearButton = {
				location: 'after',
				name: 'Clear',
				options: {
					hint: 'Clear',
					icon: 'close',
					onClick: this.clear,
					visible: !!innerSelectedItems.length
				}
			};
			return {
				source: new DataSource({
					paginate: true,
					pageSize: this.pageSize,
					load: (loadOptions) => {
						if (__DEBUG__)
							console.debug('Load dropdown data', { loadOptions });
						if (loadOptions && loadOptions.searchValue) {
							this.searchText = loadOptions.searchValue;
							loadOptions.filter = [loadOptions.searchExpr, loadOptions.searchOperation, loadOptions.searchValue];
							loadOptions.filter.filterValue = loadOptions.searchValue;
						}
						if (this.minSearchLength && `${this.searchText || ''}`.length < this.minSearchLength) {
							return Promise.resolve({ data: [] });
						}
						if (this.searchText)
							this.$emit('search', this.searchText);
						if (this.lastLoadPromise && this.lastLoadOptions && this.lastLoadOptions === JSON.stringify(loadOptions)) {
							return this.lastLoadPromise;
						}
						this.lastLoadOptions = JSON.stringify(loadOptions);
						this.lastLoadPromise = this.loadData(loadOptions)
							.then(() => {
								if (__DEBUG__)
									console.debug('Data loaded', { items: this.items, total: this.total });

								return {
									data: this.items || [],
									totalCount: this.total
								};
							})
							.finally(() => {
								setImmediate(() => {
									if (this._isDestroyed || this.opened || this.multiple) return;
									this.$refreshDropdown(true);
								});
								this.$emit('data-loaded', this.items);
							});
						return this.lastLoadPromise;
					},
					byKey: (key) => {
						if (!this.multiple)
							key = this.innerSelectedItems && this.innerSelectedItems[0] || null;
						if (__DEBUG__)
							console.debug('Dropdown get by key', { key, gbk: this.getByKey, sel: this.innerSelectedItems });
						if (typeof(key) === 'object') {
							key = key[this.valueExpr];
						}
						if (!key) return;
						let exists = this.items && _.find(this.items, (v) => v && `${v[this.valueExpr]}` === `${key}`);
						if (!exists && this.preloadedObjects) {
							if (__DEBUG__)
								console.debug('Preloaded', { pr: this.preloadedObjects, key });
							exists = _.find(this.preloadedObjects, (v) => v && `${v[this.valueExpr]}` === `${key}`);
						}
						if (exists) {
							if (__DEBUG__)
								console.debug('Dropdown by key loaded exists', { exists });
							if (!this.multiple)
								this.selectedHint = exists[this.displayExpr];
							return Promise.resolve(exists);
						}
						return this.getByKey ? this.getByKey(key) : this.loadData({ byKey: key }).then(r => {
							let resultItem = this.getItems(r, { byKey: key });
							if (Array.isArray(resultItem))
								resultItem = resultItem[0];
							if (__DEBUG__)
								console.debug('Dropdown by key loaded', { r, resultItem });
							if (!this.multiple)
								this.selectedHint = resultItem[this.displayExpr];
							return resultItem;
						});
					},
					key: this.valueExpr
				}),
				total: 0,
				items: [],
				pageIndex: 0,
				isLoading: false,
				innerSelectedItems,
				innerSelectedItemsOld: _.clone(innerSelectedItems),
				dropdownSelectedItems: [],
				clearButton,
				buttons: [...(this.showClearButton ? [clearButton] : []), 'dropDown'],
				opened: false,
				searchText: null,
				lastLoadOptions: null,
				lastLoadPromise: null,
				isRefreshing: false,
				selectedHint: null
			};
		},
		computed: {
			pagesCount() {
				return Math.ceil(this.total / this.pageSize);
			},
			selectedValue() {
				return (this.multiple ? this.innerSelectedItems : (this.displayText !== null ? this.displayText : this.innerSelectedItems[0])) || null;
			},
			isGrid() {
				return !!this.multiple || true;
			},
			searchPanelSettings() { 
				return { visible: this.searchVisible, width: 320 };
			}
		},
		watch: {
			pageIndex() {
				this.source.reload();
			},
			pageSize() {
				this.source.reload();
			},
			innerSelectedItems: {
				handler() {
					if (__DEBUG__)
						console.debug('InnerSelectedItems changed');
					if (this.opened) {
						this.processChange();
					} else {
						if (!_.isEqual(this.innerSelectedItems, this.innerSelectedItemsOld))
							this.innerSelectedItems = _.clone(this.innerSelectedItemsOld);
					}
					this.calcButtons();
				},
				deep: true
			},
			buttons: {
				handler() {
					this.$refreshDropdown(true);
				},
				deep: true
			},
			selectedItems() {
				const oldItems = this.innerSelectedItemsOld;
				const newItems = this.multiple ? (_.clone(this.selectedItems) || []) : (this.selectedItems ? [this.selectedItems] : []);
				if (JSON.stringify(oldItems) === JSON.stringify(newItems)) return;
				if (__DEBUG__)
					console.log('Selected items changed', { ph: this.placeholder, old: oldItems, new: newItems });
				this.innerSelectedItemsOld = this.multiple ? (_.clone(this.selectedItems) || []) : (this.selectedItems ? [this.selectedItems] : []);
				this.innerSelectedItems = _.clone(this.innerSelectedItemsOld);
				this.$refreshDropdown();
				this.calcButtons();
			},
			watchReload: {
				handler() {
					this.reload();
				},
				deep: true
			}
		},
		created() {
			this.$doSearch =_.debounce(this.doSearch, 500, {
				'leading': false,
				'trailing': true
			});
		},
		mounted() {
			this.$refreshDropdown = _.debounce(this.refreshDropdown, 500, {
				'leading': false,
				'trailing': true
			})
			this.$refreshDropdown();
			if (__DEBUG__)
				console.debug(`Test: Dropdown mounted ${this.placeholder}`);
		},
		methods: {
			onEditorPreparing(e) {
				if (e.parentType === 'searchPanel') {
					e.editorOptions.onValueChanged = (arg) => {
						if (arg.value.length === 0 || arg.value.length >= this.minSearchLength) {
							this.$doSearch(e.component, arg.value);
						}
					};
				}
			},
			doSearch(cmp, val) {
				this.searchText = val;
				cmp.searchByText(val);
				this.$emit('search', val);
				this.focusSearch();
			},
			setOpened(v) {
				if (__DEBUG__)
					window.dd = this;
				if (__DEBUG__)
					console.debug('Opened', v);
				this.opened = v;
				if (v) {
					this.focusSearch();
				}
				this.$emit('opened', v);

				if (this.reloadOnOpen) {
					this.reload();
				}
			},
			focusSearch() {
				setTimeout(() => {
					if (this._isDestroyed) return;
					if (this.$refs.grid && this.$refs.grid.$el)
						$(this.$refs.grid.$el).find('.dx-datagrid-search-panel .dx-texteditor-input').focus();
				}, 20);
			},
			closeDropdown() {
				if (__DEBUG__)
					console.debug('Close dropdown');
				if (this.$refs.dropdown && this.$refs.dropdown.instance && this.$refs.dropdown.instance.close) {
					this.$refs.dropdown.instance.close();
				} else if (this.$refs.selectBox && this.$refs.selectBox.instance && this.$refs.selectBox.instance.close) {
					this.$refs.selectBox.instance.close();
				}
			},
			refreshDropdown(skipIsRefreshing) {
				if (__DEBUG__)
					console.debug('Refresh dropdown');
				setImmediate(() => {
					if (this._isDestroyed) return;
					if (this.$refs.dropdown?.instance) {
						this.$refs.dropdown.instance.option('buttons', this.buttons);
						if (!this.opened && !skipIsRefreshing) {
							this.isRefreshing = true;
							this.$nextTick(() => {
								this.isRefreshing = false;
							});
						}
					} else if (this.$refs.selectBox && this.$refs.selectBox.instance) {
						this.$refs.selectBox.instance._refresh();
					}
				});
			},
			displayValueFormatter(value) {
				return `${this.displayText || value}`;
			},
			loadData(opts) {
				opts = opts || {};
				if (__DEBUG__)
					console.debug('Dropdown load data', { opts, getData: this.getData });
				if (this.getData) {
					this.isLoading = true;
					if (opts.clear) {
						this.pageIndex = 0;
					}

					const pageIndex = (opts && opts.skip ? opts.skip/opts.take : this.pageIndex);
					const pageSize = (opts && opts.pageSize ? opts.take : this.pageSize);

					return this
						.getData(
							pageIndex,
							pageSize,
							{
								...opts,
								where: this.convertFilterToSql(opts)
							}
						)
						.then(r => {
							if (this._isDestroyed) return;
							if (this.getTotal)
								this.total = this.getTotal(r, opts);
							if (this.getItems)
								this.items = this.getItems(r, opts);

							if (r && r.ErrorMessage) {
								this.$notifyError(r.ErrorMessage);
							}

							return r;
						})
						.finally(() => this.isLoading = false);
				}
				return Promise.resolve(null);
			},
			onPageChange(v) {
				if (__DEBUG__)
					console.debug('Page changed');
				this.pageIndex = v;
			},
			convertFilterToSql(opts) {
				if (!opts || !opts.filter || !opts.filter.length) return null;

				const processFilter = (f) => {
					let res;
					switch(f[1]) {
						case 'contains':
							res = `LOWER("${f[0]}") like LOWER('%${f[2]}%')`;
					}

					return res ? `(${res})` : '';
				};

				const processClause = (clause) => {
					return clause.map(f => Array.isArray(f) ? (f.hasOwnProperty('filterValue') ? processFilter(f) : processClause(f)) : f).join(' ');
				};

				const result = processClause([opts.filter]);

				if (__DEBUG__)
					console.debug('ConvertFilterToSql', { result, opts });
				return result;
			},
			reload() {
				if (this.source) {
					if (__DEBUG__)
						console.debug('Reload data');
					this.lastLoadOptions = null;
					this.lastLoadPromise = null;
					this.source.reload();
				}
			},
			processChange() {
				if (!this.opened) return;
				let newVal = _.clone(Array.isArray(this.innerSelectedItems) ? this.innerSelectedItems : []);
				let oldVal = _.clone(Array.isArray(this.innerSelectedItemsOld) ? this.innerSelectedItemsOld : []);

				let selected = newVal.filter(f => !oldVal.length || !oldVal.some(p => f === p));
				let deselected = oldVal.filter(f => !newVal.length || !newVal.some(p => f === p));

				if (__DEBUG__)
					console.debug('Dropdown selection changed', { selected, deselected, newVal, oldVal });

				if (!selected.length && !deselected.length) return;

				if (selected && selected.length) {
					this.$emit('select', selected);
				}

				if (deselected && deselected.length) {
					this.$emit('deselect', deselected);
				}

				this.innerSelectedItemsOld = newVal;

				let nv = this.multiple ? (newVal || []) : (newVal || [])[0];
				let ov = this.multiple ? (oldVal || []) : (oldVal || [])[0];

				if (__DEBUG__)
					console.debug('Dropdown changed', { nv, ov });

				this.$emit('change', nv, ov);

				if (this.$refs.dropdown && !this.multiple) this.$refs.dropdown.instance.close();
			},
			onSelectionChanged(e, processChange) {
				if (!this.opened) return;
				if (__DEBUG__)
					console.debug('onSelectionChanged', e, processChange);
				let value = this.multiple ? e.selectedRowsData : (e.selectedItem || (e.selectedRowsData && e.selectedRowsData[0]));
				this.$emit('select-data', value);
				if (processChange) {
					if (!this.multiple && value) {
						this.innerSelectedItems = [value[this.valueExpr]];
					}
				}
			},
			clear() {
				if (__DEBUG__)
					console.debug('Clear');
				this.closeDropdown();
				this.innerSelectedItemsOld = [];
				this.innerSelectedItems = [];
				this.$emit('clear');
				this.$emit('select-data', null);
				this.$emit('change', null);
				this.calcButtons();
			},
			calcButtons() {
				this.clearButton.options.visible = Array.isArray(this.selectedValue) ? this.selectedValue.length : !!this.selectedValue;
			}
		}
	};
</script>

<style lang='scss'>
	.app-core--dropdown {
		@import "main/themes/generated/variables.base.scss";

		.data-grid {
			width: 100%;
			height: 100%;
		}

		&.small {
			.dx-placeholder {
				font-size: 13px !important;
				margin-bottom: -4px;
			}

			.dx-texteditor .dx-placeholder::before {
				padding: 4px 0 4px !important;
			}

			.dx-texteditor.dx-editor-filled .dx-placeholder::before {
				padding: 4px 8px 4px !important;
			}

			input {
				font-size: 13px !important;
				padding: 4px 0 4px !important;
			}

			.dx-editor-filled {
				input {
					padding: 4px 8px 4px !important;
				}
			}

			.dx-texteditor-input-container {
				height: 28px;
			}
			.dx-texteditor-buttons-container {
				.dx-button {
					height: 20px !important;
					width: 20px !important;
					font-size: 10px !important;
					min-width: 20px !important;

					.dx-button-content {
						width: 14px;

						> * {
							font-size: 14px;
							width: 14px !important;
							height: 14px !important;
							background-size: 14px 14px;
							line-height: 14px;
						}
					}
				}
			}
		}

		.dx-datagrid-rowsview {
			border-top: none !important;
		}
	}
</style>
